import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import z from "zod";

import { get, ppost } from "@/api";
import { Models, ShopDisplayType } from "@/api/types";
import { Image, Text } from "@/atoms";
import { TermsAndCondition } from "@/atoms/terms-and-condition/TermsAndCondition";
import {
  DatePickerField,
  PhoneNumberField,
  SegmentedToggleField,
  SimpleFormProvider,
  TextAreaField,
  TextField,
  TimePickerField,
} from "@/form";
import { cn, removeNonDigits } from "@/lib/utils";
import {
  CURRENT_ORDER_QUERY_KEY,
  invalidateCurrentOrderDetails,
} from "@/modules/cart/useCartDetails";
import {
  calculatePriceInCentsForGiftCardItem,
  DiscountMap,
  GiftCardAmountOption,
} from "@/modules/giftCards";
import { useHaptics } from "@/modules/haptics/useHaptics/useHaptics";
import { PhoneVerificationCode } from "@/modules/phoneVerification";
import { LocalStorageService } from "@/modules/storage";
import {
  AccordionItem,
  AccordionList,
} from "@/molecules/accordion-list/AccordionList";
import { AddedToCartModal } from "@/organisms/added-to-cart-modal/AddedToCartModal";
import { queryClient } from "@/pages/Root";
import { Button } from "atoms/button/Button";

import { useGiftCardPhoneVerification } from "../hooks/useGiftCardPhoneVerification";

type GiftCardPurchaseFormProps = {
  defaultValues: GiftCardFormData;
  giftCards: Models<"DisplayGiftCardListing">[];
  onChange: (data: GiftCardFormData) => void;
  children?: React.ReactNode;
  linkToCart: string;
  disableSenderFields?: boolean;
  platform: Models<"PlatformModel">;
  availablePromotions?: DiscountMap;
  isAuthenticated?: boolean;
  onFormDataUpdate?: (data: GiftCardFormData) => void;
};

export const GiftCardPurchaseForm = ({
  defaultValues,
  giftCards,
  onChange,
  children,
  linkToCart,
  disableSenderFields = false,
  platform,
  availablePromotions,
  isAuthenticated = false,
  onFormDataUpdate,
}: GiftCardPurchaseFormProps) => {
  const [showAddedToCartModal, setShowAddedToCartModal] = useState(false);

  const initialFormValues = useMemo((): GiftCardFormData => {
    return {
      ...defaultValues,
      recipientType: { ...defaultValues.recipientType },
      deliveryType: { ...defaultValues.deliveryType },
    };
  }, [defaultValues]);

  const addGiftCardToCart = async (data: GiftCardFormData) => {
    const selectedGiftCardListing = giftCards?.find(
      (d) => d.id === data.giftCardListingId
    );

    const giftCardItem = selectedGiftCardListing?.giftCards.find(
      (p) => p.baseCostInCents === data.amountInCents
    );

    const giftCardPriceId = giftCardItem?.packagePriceId;
    const giftCardItemId = giftCardItem?.packageId;

    const giftCardInfo = {
      giftCardListingId: selectedGiftCardListing?.id,
      giftCardItemId: giftCardItemId,
      sharingPersonFirstName: data.recipientFirstName,
      sharingPersonLastName: data.recipientLastName,
      sharingPersonPhone: data.recipientPhoneNumber,
      sharingPersonEmail: data.recipientEmail || "",
    };

    LocalStorageService.setItem("giftCardInfo", JSON.stringify(giftCardInfo));

    const scheduledDeliveryDate =
      data.deliveryType.value === "later" &&
      data.deliveryDate &&
      data.deliveryTime
        ? dayjs(
            `${dayjs(data.deliveryDate).format("YYYY-MM-DD")} ${
              data.deliveryTime
            }`,
            "YYYY-MM-DD HH:mm"
          ).toISOString()
        : undefined;

    await ppost("/orders/current/lineitems")({
      packageId: giftCardItemId ?? "",
      packageListingId: selectedGiftCardListing?.id ?? "",
      packagePriceId: giftCardPriceId ?? "",
      giftCardDetails: {
        recipientFirstName: data.recipientFirstName,
        recipientLastName: data.recipientLastName,
        recipientEmail: data.recipientEmail || "",
        recipientPhoneNumber: `+1${removeNonDigits(
          data.recipientPhoneNumber || ""
        )}`,
        personalMessage: data.message || "",
        scheduledDeliveryDate,
        whoIsReceivingGiftCard:
          data.recipientType.value === "myself" ? "MySelf" : "SomebodyElse",
      },
    });

    await queryClient.invalidateQueries({
      queryKey: ["/orders/current/summary"],
    });
    await invalidateCurrentOrderDetails();
  };

  const handleValidationErrors = (errors: Record<string, unknown>) => {
    // Define the order of fields as they appear visually in the form
    const fieldOrder = [
      "senderFirstName",
      "senderLastName",
      "senderPhoneNumber",
      "recipientType",
      "recipientFirstName",
      "recipientLastName",
      "recipientPhoneNumber",
      "recipientEmail",
      "giftCardListingId",
      "amountInCents",
      "deliveryType",
      "deliveryDate",
      "deliveryTime",
      "message",
    ];

    const firstErrorField = fieldOrder.find((fieldName) => {
      return Object.keys(errors).some(
        (errorKey) =>
          errorKey === fieldName || errorKey.startsWith(`${fieldName}.`)
      );
    });

    if (firstErrorField) {
      let fieldElement = document.querySelector(
        `[name="${firstErrorField}"]`
      ) as HTMLElement | null;

      if (!fieldElement) {
        fieldElement = document.querySelector(
          `[data-field="${firstErrorField}"]`
        ) as HTMLElement | null;
      }

      if (fieldElement) {
        const focusableSelector =
          "input, select, textarea, button, [contenteditable='true'], [tabindex]:not([tabindex='-1'])";

        const focusTarget = fieldElement.matches(focusableSelector)
          ? fieldElement
          : (fieldElement.querySelector(
              focusableSelector
            ) as HTMLElement | null);

        const scrollTarget = focusTarget ?? fieldElement;

        scrollTarget.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        if (focusTarget) {
          setTimeout(() => {
            focusTarget.focus({ preventScroll: true });
          }, 300);
        }
      }
    }
  };

  const {
    showPhoneVerification,
    verificationError,
    isVerifying,
    smsSendError,
    isSendingSms,
    pendingFormData,
    handleFormSubmit,
    handlePhoneVerification,
    handleResendCode,
    setVerificationCode,
    resetVerification,
  } = useGiftCardPhoneVerification({
    isAuthenticated,
    onSuccess: addGiftCardToCart,
    onFormDataUpdate,
    onShowModal: () => setShowAddedToCartModal(true),
  });

  const handleSuccess = async () => {
    setShowAddedToCartModal(true);
  };

  const handleAddAnotherGiftCard = () => {
    const resetPayload: GiftCardFormData = {
      ...initialFormValues,
      recipientType: { ...initialFormValues.recipientType },
      deliveryType: { ...initialFormValues.deliveryType },
    };

    onChange(resetPayload);
    onFormDataUpdate?.(resetPayload);
    resetVerification();
  };

  return (
    <div className="GiftCardPurchaseForm">
      {showPhoneVerification ? (
        <div className="flex h-[70dvh] items-center justify-center bg-[#FAFAFA] p-4 lg:h-[80dvh]">
          <div className="w-full max-w-md">
            <PhoneVerificationCode
              onChange={(code: string) => {
                setVerificationCode(code);
              }}
              onFinalChange={(code: string) => {
                handlePhoneVerification(code);
              }}
              onResend={handleResendCode}
              phoneNumber={pendingFormData?.senderPhoneNumber}
              error={verificationError}
              disabled={isVerifying}
              isVerifying={isVerifying}
              smsSendError={smsSendError}
              onBackToForm={resetVerification}
            />
          </div>
        </div>
      ) : (
        <div className={isSendingSms ? "pointer-events-none opacity-50" : ""}>
          <SimpleFormProvider
            schema={giftCardFormSchema}
            defaultValues={defaultValues}
            onSubmit={handleFormSubmit}
            onSuccess={handleSuccess}
            onChange={onChange}
            onValidationErrors={handleValidationErrors}
            fullScreenLoader={true}
            resetFields={false}
          >
            <FormContent
              giftCards={giftCards}
              disableSenderFields={disableSenderFields}
              availablePromotions={availablePromotions}
            >
              {children}
            </FormContent>
            <AddToCartModalWithCount
              open={showAddedToCartModal}
              onClose={() => {
                setShowAddedToCartModal(false);
                // Scroll to top when "Add Another" is clicked
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              linkToCart={linkToCart}
              resetValues={initialFormValues}
              onAddAnother={handleAddAnotherGiftCard}
            />
          </SimpleFormProvider>
          <div className="mx-2">
            <TermsAndCondition hideText={true} />
          </div>
          <div className="mx-2 mt-4 rounded-md bg-white p-4 shadow-subtle">
            <Text
              text="FAQ"
              variant="body1"
              component="h2"
              className="mb-4 text-bold2"
            />
            <AccordionList items={giftCardFaqItems(platform)} />
          </div>
          <div className="pb-4" />{" "}
          {/* Add space for the bottom, with the same background color */}
        </div>
      )}
    </div>
  );
};

type FormContentProps = {
  giftCards: Models<"DisplayGiftCardListing">[] | undefined;
  children: React.ReactNode;
  disableSenderFields?: boolean;
  availablePromotions?: DiscountMap;
};

const FormContent = ({
  giftCards,
  children,
  disableSenderFields,
  availablePromotions,
}: FormContentProps) => {
  const { triggerHaptic } = useHaptics();
  const { field: giftCardListingIdField } = useController({
    name: "giftCardListingId",
  });

  const { field: amountInCentsField } = useController({
    name: "amountInCents",
  });

  const selectedGiftCardListing = giftCards?.find(
    (giftCard) => giftCard.id === giftCardListingIdField.value
  );

  const selectedGiftCardItem = selectedGiftCardListing?.giftCards.find(
    (giftCard) => giftCard.baseCostInCents === amountInCentsField.value
  );

  const { watch } = useFormContext<GiftCardFormData>();

  useEffect(() => {
    if (selectedGiftCardItem) {
      amountInCentsField.onChange(selectedGiftCardItem.baseCostInCents);
    }
  }, [selectedGiftCardItem, amountInCentsField]);

  const recipientTypeField = watch("recipientType");
  const deliveryTypeField = watch("deliveryType");
  const messageField = watch("message") ?? "";
  const userTimezone = (() => {
    const shortTz = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;

    // If it starts with GMT, replace with UTC for modern formatting
    if (shortTz?.startsWith("GMT")) {
      return shortTz.replace("GMT", "UTC");
    }

    // Convert 3-letter timezone codes to 2-letter by omitting the middle letter
    // PST -> PT, PDT -> PT, EST -> ET, EDT -> ET
    if (shortTz && shortTz.length === 3 && /^[A-Z]{3}$/.test(shortTz)) {
      return shortTz[0] + shortTz[2];
    }

    return shortTz;
  })();

  const renderDesignSelection = () => (
    <div className="flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4">
      <Text
        className="text-bold2 text-black"
        text="Pick a design"
        variant="body1"
      />
      <div className="grid grid-cols-2 gap-4">
        {giftCards?.map((giftCard) => (
          <button
            key={giftCard.id}
            type="button"
            onClick={() => {
              triggerHaptic("secondary");
              giftCardListingIdField.onChange(giftCard.id);
            }}
            data-field="giftCardListingId"
            className={cn(
              "relative overflow-hidden rounded-lg shadow-[0px_2px_4px_rgba(0,0,0,0.15)] transition-all",
              giftCardListingIdField.value === giftCard.id
                ? "ring-[3px] ring-brand-color"
                : "border border-gray-300"
            )}
          >
            <Image
              alt="Gift Card background"
              className="relative inset-0 z-0 aspect-[16/9]"
              src={giftCard.imageUri}
              width={170}
              height={110}
            />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="GiftCardPurchaseForm flex flex-col gap-4 ">
      <div className="flex flex-col gap-4 px-2 ">
        <div className="flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4">
          <Text
            className="text-bold2 text-black"
            text="Who is this for?"
            variant="body1"
          />
          <SegmentedToggleField
            options={recipientOptions}
            name="recipientType"
            label="Recipient Type"
            hideValidation
            hideLabel
          />
        </div>

        {renderDesignSelection()}

        <div className="flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4">
          {recipientTypeField.value === "someone-else" && (
            <>
              <Text
                className="text-bold2 text-black"
                text="Whose day are you making?"
                variant="body1"
              />

              <div className="flex flex-col gap-2">
                <div className="flex gap-4">
                  <TextField
                    name="recipientFirstName"
                    label="Recipient First Name"
                    placeholder="First name"
                    hideLabel
                    hideValidation
                  />
                  <TextField
                    name="recipientLastName"
                    label="Recipient Last Name"
                    placeholder="Last name"
                    hideLabel
                    hideValidation
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <PhoneNumberField
                    name="recipientPhoneNumber"
                    label={"Recipient Phone number"}
                    hideLabel
                    placeholder="(123) 456-7890"
                    hideValidation
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <TextField
                    name="recipientEmail"
                    label="Recipient Email"
                    placeholder="Email (optional)"
                    hideLabel
                    hideValidation
                  />
                </div>
              </div>
            </>
          )}

          <Text
            className="text-bold2 text-black"
            text="Pick an amount"
            variant="body1"
          />
          <div className="grid grid-cols-2 gap-4">
            {selectedGiftCardListing?.giftCards
              ?.sort((a, b) => a.baseCostInCents - b.baseCostInCents)
              .map((giftCardItem) => {
                const discount =
                  availablePromotions?.[giftCardItem.packageId] ?? undefined;
                const priceInformation = calculatePriceInCentsForGiftCardItem({
                  basePriceCents: giftCardItem.baseCostInCents,
                  percentageOff: discount?.maxPercentageOff ?? 0,
                  amountOffInCents: discount?.maxAmountOff ?? 0,
                });

                return (
                  <GiftCardAmountOption
                    key={giftCardItem.packageId}
                    amount={Number(giftCardItem.baseCostInCents)}
                    value={selectedGiftCardItem?.baseCostInCents ?? 0}
                    onSelect={amountInCentsField.onChange}
                    priceInformation={{
                      packageId: giftCardItem.packageId,
                      packagePriceId: giftCardItem.packagePriceId,
                      originalPrice: giftCardItem.baseCostInCents,
                      price: priceInformation,
                      discountedPrice: priceInformation,
                      discountAmount: discount?.maxAmountOff ?? 0,
                      discountPercentage:
                        (discount?.maxPercentageOff ?? 0) * 100,
                      isPercentage: (discount?.maxPercentageOff ?? 0) > 0,
                      isAmount: (discount?.maxAmountOff ?? 0) > 0,
                      isBuyXGetY: discount?.isBuyXGetY ?? false,
                      packageName:
                        discount?.discount?.inclusions?.[0]?.packageName ?? "",
                    }}
                  />
                );
              })}
          </div>

          {recipientTypeField.value === "someone-else" && (
            <div className="mt-4 flex flex-col gap-4">
              <Text
                className="text-bold2 text-black"
                text="When should we send it?"
                variant="body1"
              />

              <SegmentedToggleField
                name="deliveryType"
                label="Delivery Type"
                options={deliveryOptions}
                hideValidation
                hideLabel
              />

              {deliveryTypeField.value === "later" && (
                <div className="flex gap-4">
                  <DatePickerField
                    name="deliveryDate"
                    label="Delivery date"
                    placeholder="Date"
                    minDate={new Date()}
                  />
                  <TimePickerField
                    name="deliveryTime"
                    label={`Delivery time (${userTimezone})`}
                    placeholder="Time"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={cn(
            "flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4",
            disableSenderFields && "hidden"
          )}
        >
          <Text
            className="text-bold2 text-black"
            text="Who is this from?"
            variant="body1"
          />
          <div className="flex flex-col gap-2">
            <div className="flex gap-4">
              <TextField
                name="senderFirstName"
                label="Sender First Name"
                placeholder="First name"
                disabled={disableSenderFields}
                hideLabel
                hideValidation
              />
              <TextField
                name="senderLastName"
                label="Sender Last Name"
                placeholder="Last name"
                disabled={disableSenderFields}
                hideLabel
                hideValidation
              />
            </div>
            <div className="flex flex-col">
              <PhoneNumberField
                name="senderPhoneNumber"
                label="Sender Phone number"
                placeholder="(123) 456-7890"
                disabled={disableSenderFields}
                hideLabel
                hideValidation
              />
            </div>
            <div className="flex flex-col">
              <TextField
                name="senderEmail"
                label="Sender Email"
                placeholder="Email (optional)"
                disabled={disableSenderFields}
                hideLabel
                hideValidation
              />
            </div>
          </div>
        </div>

        {recipientTypeField.value === "someone-else" && (
          <div className="flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4">
            <Text
              className="text-bold2 text-black"
              text="Message"
              variant="body1"
            />
            <TextAreaField
              name="message"
              label="Message"
              placeholder="Gift message (optional)"
              hint={`${messageField?.length || 0}/200`}
              hideLabel={true}
              hideValidation={true}
              onFocus={(event) => {
                event.currentTarget.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }}
            />
          </div>
        )}
      </div>
      {children}

      <div
        className={cn(
          "sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] z-10 w-full bg-gray-50 p-4",
          globalThis.location.pathname.includes("gift-cards/purchase") &&
            "lg:-bottom-6"
        )}
      >
        <Button submit fullWidth style="primary">
          Add to Cart
        </Button>
      </div>
    </div>
  );
};

const giftCardFormSchema = z
  .object({
    recipientType: z.object(
      {
        value: z.enum(["someone-else", "myself"]),
        label: z.string(),
      },
      { message: "Recipient type is required" }
    ),
    senderFirstName: z.string(),
    senderLastName: z.string(),
    senderEmail: z.string().optional(),
    senderPhoneNumber: z.string(),
    recipientFirstName: z.string(),
    recipientLastName: z.string(),
    recipientEmail: z.string().optional(),
    recipientPhoneNumber: z.string(),
    message: z
      .string()
      .max(200, { message: "Message must be less than 200 characters" })
      .optional(),
    giftCardListingId: z.string(),
    amountInCents: z.number(),
    deliveryType: z.object(
      {
        value: z.enum(["immediate", "later"]),
        label: z.string(),
      },
      { message: "Delivery type is required" }
    ),
    deliveryDate: z.date().optional(),
    deliveryTime: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.recipientType.value === "someone-else") {
        if (!data.recipientPhoneNumber) return false;
        const phoneDigits = data.recipientPhoneNumber.replaceAll(/\D/g, "");
        return phoneDigits.length === 10;
      }
      return true;
    },
    {
      message: "Must be a valid phone number",
      path: ["recipientPhoneNumber"],
    }
  )
  .refine(
    (data) => {
      if (data.recipientType.value === "someone-else") {
        if (!data.recipientFirstName) return false;
      }
      return true;
    },
    {
      message: "Recipient first name is required",
      path: ["recipientFirstName"],
    }
  )
  .refine(
    (data) => {
      if (data.recipientType.value === "someone-else") {
        if (!data.recipientLastName) return false;
      }
      return true;
    },
    {
      message: "Recipient last name is required",
      path: ["recipientLastName"],
    }
  )
  .refine(
    (data) => {
      if (!data.senderFirstName) return false;
      return true;
    },
    {
      message: "Sender first name is required",
      path: ["senderFirstName"],
    }
  )
  .refine(
    (data) => {
      if (!data.senderLastName) return false;
      return true;
    },
    {
      message: "Sender last name is required",
      path: ["senderLastName"],
    }
  )
  .refine(
    (data) => {
      if (!data.senderPhoneNumber) return false;
      const phoneDigits = data.senderPhoneNumber.replaceAll(/\D/g, "");
      return phoneDigits.length === 10;
    },
    {
      message: "Must be a valid phone number",
      path: ["senderPhoneNumber"],
    }
  )
  .refine(
    (data) => {
      if (data.deliveryType.value === "later") {
        if (!data.deliveryDate) return false;
      }
      return true;
    },
    {
      message: "Delivery date is required",
      path: ["deliveryDate"],
    }
  )
  .refine(
    (data) => {
      if (data.deliveryType.value === "later") {
        if (!data.deliveryTime) return false;
      }
      return true;
    },
    {
      message: "Delivery time is required",
      path: ["deliveryTime"],
    }
  );

export type GiftCardFormData = z.infer<typeof giftCardFormSchema>;

const recipientOptions = [
  { value: "someone-else", label: "Someone Else" },
  { value: "myself", label: "Myself" },
];

const deliveryOptions = [
  { value: "immediate", label: "Immediate" },
  { value: "later", label: "Later" },
];

const giftCardFaqItems = (
  platform: Models<"PlatformModel">
): AccordionItem[] => [
  {
    title: "How can I purchase a gift card through the app?",
    children: (
      <div className="text-body3 text-primary">
        <p>
          Go to the Gift Cards section in the main menu. From there, you can:
        </p>
        <ul className="mt-2 list-disc pl-5">
          <li>Choose a preset amount</li>
          <li>Select a recipient (yourself or someone else)</li>
          <li>Pay securely using your preferred payment method</li>
        </ul>
      </div>
    ),
    titleClassName: "text-bold2",
  },
  {
    title: "Can I send a gift card to someone else?",
    children: (
      <div className="text-body3 text-primary">
        Yes. When purchasing, you&apos;ll choose whether the gift card is for
        you or for someone else.
        <ul className="mt-2 list-disc pl-5">
          <li>
            If it&apos;s for someone else, you&apos;ll enter their name, phone
            number, and an optional message.
          </li>
          <li>
            They&apos;ll receive the gift card instantly by text, or at your
            scheduled delivery time (if scheduling is available).
          </li>
        </ul>
      </div>
    ),
    titleClassName: "text-bold2",
  },
  {
    title:
      "Can I finance a gift card purchase through Affirm or other payment plans?",
    children: (
      <div className="text-body3 text-primary">
        No. Gift cards must be paid in full at the time of purchase. Financing
        options like Affirm cannot be used.
      </div>
    ),
    titleClassName: "text-bold2",
  },
  {
    title: "How does the recipient receive and claim a gift card?",
    children: (
      <div className="text-body3 text-primary">
        <ul className="list-disc pl-5">
          <li>
            If you purchase for yourself, the amount is added to your account as
            RepeatCash right away.
          </li>
          <li>
            If you purchase for someone else, they&apos;ll get a text message
            with the gift card on the scheduled date. Once they claim it, the
            balance is added to their RepeatCash account.
          </li>
        </ul>
      </div>
    ),
    titleClassName: "text-bold2",
  },
  {
    title: "What if I send a gift card to the wrong person?",
    children: (
      <div className="text-body3 text-primary">
        Please contact {platform?.name || "[Business Name]"} immediately so they
        can help you update the recipient information.
      </div>
    ),
    titleClassName: "text-bold2",
  },
  {
    title: "Do gift cards purchased through this app expire?",
    children: (
      <div className="text-body3 text-primary">
        No. Gift cards purchased through this app never expire. The balance will
        remain in the recipient&apos;s account until it&apos;s used.
      </div>
    ),
    titleClassName: "text-bold2",
  },
  {
    title: "Can I use my Gift Card in the office?",
    children: (
      <div className="text-body3 text-primary">
        Yes! Gift cards can be used both in this app and at the office. Just let
        the staff know you&apos;d like to pay with your gift card, and the
        balance will be applied to your checkout.
      </div>
    ),
    titleClassName: "text-bold2",
  },
];

const numberToWords: Record<number, string> = {
  1: "one",
  2: "two",
  3: "three",
  4: "four",
  5: "five",
  6: "six",
  7: "seven",
  8: "eight",
  9: "nine",
  10: "ten",
  11: "eleven",
  12: "twelve",
};

type AddToCartModalWithCountProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly linkToCart: string;
  readonly resetValues: GiftCardFormData;
  readonly onAddAnother: () => void;
};

function AddToCartModalWithCount({
  open,
  onClose,
  linkToCart,
  resetValues,
  onAddAnother,
}: AddToCartModalWithCountProps): React.ReactElement {
  const { reset } = useFormContext<GiftCardFormData>();
  const { data: cartDetails } = useQuery({
    queryKey: ["/orders/current/summary", { includePackageLineItems: true }],
    queryFn: () =>
      get("/orders/current/summary", { includePackageLineItems: true }),
    enabled: open,
  });

  const { data: transfers } = useQuery({
    queryKey: [
      "/gift-cards/transfers/by-order/{orderId}",
      cartDetails?.shopDisplayOrderId,
    ],
    queryFn: () =>
      get("/gift-cards/transfers/by-order/{orderId}", {
        orderId: cartDetails?.shopDisplayOrderId ?? "",
      }),
    enabled: !!cartDetails?.shopDisplayOrderId && open,
  });

  const numberOfGiftCardsInCart = useMemo(() => {
    return (
      cartDetails?.shopDisplayOrderLineItems?.filter(
        (item) => item.displayType === ShopDisplayType.GiftCard
      ).length ?? 0
    );
  }, [cartDetails]);

  const latestGiftCard = useMemo(() => {
    const giftCards =
      cartDetails?.shopDisplayOrderLineItems?.filter(
        (item) => item.displayType === ShopDisplayType.GiftCard
      ) || [];

    return giftCards.at(-1);
  }, [cartDetails]);

  const transfer = transfers?.find(
    (transfer: Models<"DisplayGiftCardTransferItem">) =>
      transfer.id === latestGiftCard?.packageTransferId
  );

  useEffect(() => {
    if (latestGiftCard?.packageTransferId) {
      queryClient.invalidateQueries({
        queryKey: [
          "/gift-cards/transfers/by-order/{orderId}",
          cartDetails?.shopDisplayOrderId,
        ],
      });
    }
  }, [latestGiftCard, cartDetails?.shopDisplayOrderId]);

  const getNumberDisplay = (num: number): string => {
    return numberToWords[num] || num.toString();
  };

  return (
    <AddedToCartModal
      title={`${latestGiftCard?.name}`}
      open={open && !!transfer}
      subtitle={`For: ${transfer?.recipientInformation}`}
      cost={Number(latestGiftCard?.cost ?? 0)}
      membership={null}
      firstButtonLink={linkToCart}
      onClose={onClose}
      giftCard={{
        label: `Why stop at ${getNumberDisplay(
          numberOfGiftCardsInCart
        )}? Add another gift card`,
        onGiftCardClick: () => {
          reset({
            ...resetValues,
            recipientType: { ...resetValues.recipientType },
            deliveryType: { ...resetValues.deliveryType },
          });
          window.scrollTo({ top: 0, behavior: "smooth" });
          onAddAnother();
          onClose();
        },
      }}
    />
  );
}
